/**
 * ManualReviewNotice — what happens next when an answer routes to a human.
 *
 * The insurer's web service refuses a politically exposed person outright
 * (error `0284`, see docs/audit/QUALITAS_ANALISIS.md). That is a business
 * outcome, not a mistake the user made, so this is deliberately NOT an error
 * surface: informational colour, no red, no blocked submit. It explains the
 * manual underwriting path so the answer never reads as a dead end.
 */
import { View, Text } from 'react-native'
import { Info } from '@/components/ui/icons'
import { useTheme } from '@/lib/hooks/use-theme'
import { useSemanticColors, SPACING, RADIUS } from '@/lib/theme/tokens'
import { FONTS } from '@/lib/brand'

interface ManualReviewNoticeProps {
  title: string
  body: string
  testID: string
}

export function ManualReviewNotice({ title, body, testID }: ManualReviewNoticeProps) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)

  return (
    <View
      testID={testID}
      accessibilityRole="summary"
      style={{
        flexDirection: 'row',
        gap: SPACING.sm,
        padding: SPACING.md,
        borderRadius: RADIUS.lg,
        borderWidth: 1,
        borderColor: colors.border,
        backgroundColor: colors.surfaceSubtle,
      }}
    >
      <Info size={20} color={colors.info} strokeWidth={1.8} />
      <View style={{ flex: 1, gap: 4 }}>
        <Text style={{ fontSize: 15, fontFamily: FONTS.body, color: colors.textPrimary }}>
          {title}
        </Text>
        <Text
          style={{
            fontSize: 14,
            lineHeight: 20,
            fontFamily: FONTS.body,
            color: colors.textSecondary,
          }}
        >
          {body}
        </Text>
      </View>
    </View>
  )
}
